import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const PortalContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PortalProvider({ children }) {
  const { user, isAuthenticated, token } = useAuth();
  const [portalProfile, setPortalProfile] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [resumeData, setResumeData] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch portal profile
  const fetchPortalProfile = useCallback(async () => {
    if (!isAuthenticated) {
      setPortalProfile(null);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/portal/auth/me`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (response.ok) {
        const data = await response.json();
        setPortalProfile(data);
      } else {
        setPortalProfile(null);
      }
    } catch (err) {
      console.error('Failed to fetch portal profile:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  // Fetch enrollments
  const fetchEnrollments = useCallback(async () => {
    if (!isAuthenticated) {
      setEnrollments([]);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/portal/enrollments/my`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (response.ok) {
        const data = await response.json();
        setEnrollments(data.enrollments || []);
      }
    } catch (err) {
      console.error('Failed to fetch enrollments:', err);
    }
  }, [isAuthenticated, token]);

  // Fetch enrolled courses
  const fetchCourses = useCallback(async () => {
    if (!isAuthenticated) {
      setCourses([]);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/portal/enrollments/my/courses`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (response.ok) {
        const data = await response.json();
        setCourses(data.courses || []);
      }
    } catch (err) {
      console.error('Failed to fetch courses:', err);
    }
  }, [isAuthenticated, token]);

  // Fetch resume lesson data
  const fetchResumeData = useCallback(async () => {
    if (!isAuthenticated) {
      setResumeData(null);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/portal/enrollments/my/resume`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (response.ok) {
        const data = await response.json();
        setResumeData(data);
      }
    } catch (err) {
      console.error('Failed to fetch resume data:', err);
    }
  }, [isAuthenticated, token]);

  // Fetch announcements
  const fetchAnnouncements = useCallback(async () => {
    if (!isAuthenticated) {
      setAnnouncements([]);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/portal/announcements/my`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (response.ok) {
        const data = await response.json();
        setAnnouncements(data.announcements || []);
      }
    } catch (err) {
      console.error('Failed to fetch announcements:', err);
    }
  }, [isAuthenticated, token]);

  // Mark announcement as read
  const markAnnouncementRead = async (announcementId) => {
    try {
      await fetch(`${API_URL}/api/portal/announcements/${announcementId}/read`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      // Update local state
      setAnnouncements(prev => 
        prev.map(a => a.announcement_id === announcementId ? {...a, is_read: true} : a)
      );
    } catch (err) {
      console.error('Failed to mark announcement as read:', err);
    }
  };

  // Enroll in a program
  const enrollInProgram = async (programId, cohortId = null) => {
    try {
      const response = await fetch(`${API_URL}/api/portal/enrollments/enroll`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ program_id: programId, cohort_id: cohortId })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to enroll');
      }

      const enrollment = await response.json();
      
      // Refresh enrollments
      await fetchEnrollments();
      await fetchCourses();
      
      return { success: true, enrollment };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Update lesson progress
  const updateLessonProgress = async (lessonId, completed = true, timeSpent = 0) => {
    try {
      const response = await fetch(`${API_URL}/api/portal/enrollments/progress`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          lesson_id: lessonId,
          completed,
          time_spent_seconds: timeSpent
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update progress');
      }

      const result = await response.json();
      
      // Refresh courses to update progress
      await fetchCourses();
      await fetchResumeData();
      
      return { success: true, ...result };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Get course progress
  const getCourseProgress = async (courseId) => {
    try {
      const response = await fetch(`${API_URL}/api/portal/enrollments/progress/${courseId}`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (!response.ok) {
        throw new Error('Failed to fetch progress');
      }

      return await response.json();
    } catch (err) {
      console.error('Failed to fetch course progress:', err);
      return null;
    }
  };

  // Load initial data when authenticated
  useEffect(() => {
    fetchPortalProfile();
  }, [fetchPortalProfile]);

  useEffect(() => {
    if (portalProfile) {
      fetchEnrollments();
      fetchCourses();
      fetchResumeData();
      fetchAnnouncements();
    }
  }, [portalProfile, fetchEnrollments, fetchCourses, fetchResumeData, fetchAnnouncements]);

  // Helper getters
  const isStudent = portalProfile?.role === 'student' || portalProfile?.role === 'instructor' || portalProfile?.role === 'admin';
  const isInstructor = portalProfile?.role === 'instructor' || portalProfile?.role === 'admin';
  const isAdmin = portalProfile?.role === 'admin';
  const unreadAnnouncementsCount = announcements.filter(a => !a.is_read).length;

  const value = {
    // State
    portalProfile,
    enrollments,
    courses,
    resumeData,
    announcements,
    unreadAnnouncementsCount,
    loading,
    error,
    
    // Role checks
    isStudent,
    isInstructor,
    isAdmin,
    
    // Actions
    fetchPortalProfile,
    fetchEnrollments,
    fetchCourses,
    fetchResumeData,
    fetchAnnouncements,
    enrollInProgram,
    updateLessonProgress,
    getCourseProgress,
    markAnnouncementRead,
    
    // Refresh all
    refresh: async () => {
      await fetchPortalProfile();
      await fetchEnrollments();
      await fetchCourses();
      await fetchResumeData();
      await fetchAnnouncements();
    }
  };

  return (
    <PortalContext.Provider value={value}>
      {children}
    </PortalContext.Provider>
  );
}

export function usePortal() {
  const context = useContext(PortalContext);
  if (!context) {
    throw new Error('usePortal must be used within a PortalProvider');
  }
  return context;
}
